'use client'

import { Text } from '@vinted/web-ui'
import { ReactNode, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { v4 as uuid } from 'uuid'

import {
  useItemUploadContext,
  UiStateContext,
  useAttributesContext,
} from '@marketplace-web/item-upload/item-upload-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import {
  clickEvent,
  clickBundleDiscountSuggestionEvent,
  UserBundleDiscountDto,
  closeBundleDiscountScreenEvent,
} from '@marketplace-web/bundles/items-bundle-data'

import BundleDiscountsModal from './BundleDiscountsModal'

const BundleDiscountsHint = () => {
  const translationKeyId = useMemo(() => uuid(), [])
  const { track } = useTracking()
  const translate = useTranslate('item_upload.price.bundle_discounts_hint')

  const { locale } = useIntl()
  const { user } = useSession()
  const {
    configuration: { currency, isFirstTimeLister },
    isABTestEnabled,
  } = useItemUploadContext()
  const {
    attributes: { price },
  } = useAttributesContext()
  const { screen } = useContext(UiStateContext)

  const [userBundleDiscounts, setUserBundleDiscounts] = useState(user?.bundle_discount)
  const [isDiscountsModalOpen, setIsDiscountsModalOpen] = useState(false)
  const [disabledDiscountTipWasShown, setDisabledDiscountTipWasShown] = useState(false)

  const highiestTierDiscount = userBundleDiscounts?.discounts[2]!.fraction

  const bundleDiscountVisibilityListingFormABTestEnabled = isABTestEnabled(
    'bundle_discount_visibility_listing_form',
  )

  const shouldRenderDisabledDiscountTip =
    !isFirstTimeLister &&
    bundleDiscountVisibilityListingFormABTestEnabled &&
    !userBundleDiscounts?.enabled &&
    !disabledDiscountTipWasShown

  const handleBundleDiscountsLinkClick = () => {
    track(
      clickEvent({
        target: 'bundle_discount_settings',
        targetDetails: JSON.stringify({
          enabled: userBundleDiscounts?.enabled,
          discount_up_to: highiestTierDiscount,
          price,
          currency,
        }),
      }),
    )

    setIsDiscountsModalOpen(true)
  }

  const handleTipLinkClick = () => {
    track(
      clickBundleDiscountSuggestionEvent({
        discountsEnabled: userBundleDiscounts?.enabled ?? false,
        screen,
      }),
    )

    setIsDiscountsModalOpen(true)
  }

  const getHintTranslation = () => {
    const highiestTierDiscountNumber = Number(highiestTierDiscount)

    const hintLinkValue = {
      'bundle-discounts-link': (chunks: Array<ReactNode>) => (
        <button key={translationKeyId} onClick={handleBundleDiscountsLinkClick} type="button">
          <Text underline clickable type={Text.Type.Caption} as="span">
            {chunks}
          </Text>
        </button>
      ),
    }

    if (!userBundleDiscounts?.enabled) return translate('off_title', hintLinkValue)

    const discountPercentage = highiestTierDiscountNumber.toLocaleString(locale, {
      style: 'percent',
    })

    return translate('on_title', {
      ...hintLinkValue,
      percentage: discountPercentage,
    })
  }

  const getHintTipTranslation = () => {
    const hintTipLinkValue = {
      'bundle-discounts-link': (chunks: Array<ReactNode>) => (
        <button key={translationKeyId} onClick={handleTipLinkClick} type="button">
          <Text underline clickable type={Text.Type.Caption} as="span">
            {chunks}
          </Text>
        </button>
      ),
    }

    return translate('suggest_turn_on_title', hintTipLinkValue)
  }

  const renderContent = () =>
    shouldRenderDisabledDiscountTip ? (
      <Text type={Text.Type.Caption} as="p" html>
        {getHintTipTranslation()}
      </Text>
    ) : (
      <Text type={Text.Type.Caption} as="p" testId="bundle-discounts-hint" html>
        {getHintTranslation()}
      </Text>
    )

  const handleBundleDiscountModalClose = (fractions?: Array<string | undefined>) => {
    setIsDiscountsModalOpen(false)

    if (userBundleDiscounts?.enabled) {
      setDisabledDiscountTipWasShown(true)
    }

    if (userBundleDiscounts?.enabled && fractions) {
      track(
        closeBundleDiscountScreenEvent({
          discount2Items: Number(fractions[0]) * 100 || null,
          discount3Items: Number(fractions[1]) * 100 || null,
          discount5Items: Number(fractions[2]) * 100 || null,
          screen,
        }),
      )
    }
  }

  const handleBundleDiscountUpdate = (bundleDiscount?: UserBundleDiscountDto | null) => {
    setUserBundleDiscounts(bundleDiscount)

    if (!bundleDiscount?.enabled) {
      setDisabledDiscountTipWasShown(true)
    }
  }

  return (
    <>
      {renderContent()}
      <BundleDiscountsModal
        show={isDiscountsModalOpen}
        onClose={fractions => handleBundleDiscountModalClose(fractions)}
        bundleDiscount={userBundleDiscounts}
        onBundleDiscountUpdate={bundleDiscount => handleBundleDiscountUpdate(bundleDiscount)}
      />
    </>
  )
}

export default BundleDiscountsHint
